import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createConfiguration } from "../../../redux/Configuration/actions";
import { getAllSessions } from "../../../redux/Session/actions";
import { getAllEvents } from "../../../redux/Event/actions";
import { fetchMachines } from "../../../redux/Machine/actions";
import { getAllUsers } from "../../../redux/User/actions";
import { Button, Form, Input, Switch, Select, message } from "antd";
import ImageUpload from "./ImageUpload";
import { SettingDictionary } from "../../../constants/ModeSetting";
import PrintSettings from "./AddConfiguration/PrintSettings";
import ShareSetting from "./AddConfiguration/ShareSetting";
import GameSetting from "./AddConfiguration/GameSetting"; // Import GameSetting

const { Option } = Select;

const AddConfiguration = () => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.configuration || {});
  const sessions = useSelector((state) => state.sessions?.data || []);
  const events = useSelector((state) => state.events?.data || []);
  const machines = useSelector((state) => state.machines?.machines || []);
  const users = useSelector((state) => state.Users?.data || []);
  const [form] = Form.useForm();

  const [overlayImages, setOverlayImages] = useState([]);
  const [gifOverlays, setGifOverlays] = useState([]);
  const [greenScreenBackgrounds, setGreenScreenBackgrounds] = useState([]);
  const [printSettings, setPrintSettings] = useState({});
  const [smsConfig, setSmsConfig] = useState({ SMS: false });
  const [facebookConfig, setFacebookConfig] = useState({
    appId: "",
    pageId: "",
    appSecret: "",
    facebookToken: "",
  });

  // Local state for GameSetting
  const [game, setGame] = useState(false);
  const [winners, setWinners] = useState([]);

  useEffect(() => {
    dispatch(getAllSessions());
    dispatch(getAllEvents());
    dispatch(fetchMachines());
    dispatch(getAllUsers());
    resetForm();
  }, [dispatch]);

  const resetForm = () => {
    form.resetFields();
    setOverlayImages([]);
    setGifOverlays([]);
    setGreenScreenBackgrounds([]);
    setPrintSettings({});
    setSmsConfig({ SMS: false });
    setFacebookConfig({
      appId: "",
      pageId: "",
      appSecret: "",
      facebookToken: "",
    });
    setGame(false);
    setWinners([]);
  };

  const handlePrintSettingsChange = (settings) => {
    setPrintSettings(settings);
  };

  const handleSmsChange = (config) => {
    setSmsConfig(config);
  };

  const handleFacebookChange = (config) => {
    setFacebookConfig(config);
  };


  const onFinish = (values) => {
    let winnersString = null;
  
    if (game && winners.length > 0) {
      winnersString = winners.join(', '); // Combine winner names into a comma-separated string
    }
  
    const formData = {
      ...values,
      overlayImages,
      gifOverlays,
      greenScreenBackgrounds,
      ...printSettings,
      ...smsConfig,
      ...facebookConfig,
      game,
      winners: winnersString || winners, // Save either the string or the array based on the game status
    };
  
    dispatch(createConfiguration(formData));
    message.success("Configuration created successfully!");
    resetForm();
  };
  
  const onFinishFailed = (errorInfo) => {
    console.error("Form submission failed:", errorInfo);
    message.error("Please correct the errors in the form.");
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
    >
      <Form.Item
        label="Configuration Name"
        name="confName"
        rules={[
          { required: true, message: "Please input the configuration name!" },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item label="Active" name="isActive" valuePropName="checked">
        <Switch />
      </Form.Item>

      <Form.Item
        label="Setting"
        name="setting"
        rules={[{ required: true, message: "Please select a setting!" }]}
      >
        <Select placeholder="Select a setting">
          {Object.keys(SettingDictionary).map((key) => (
            <Option key={key} value={key}>
              {SettingDictionary[key]}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Theme"
        name="theme"
        rules={[{ required: true, message: "Please select a theme!" }]}
      >
        <Select placeholder="Select a theme">
          <Option value="DARK">Dark</Option>
          <Option value="LIGHT">Light</Option>
        </Select>
      </Form.Item>

      <Form.Item
        label="Session"
        name="session"
        rules={[{ required: true, message: "Please select a session!" }]}
      >
        <Select placeholder="Select a session">
          {sessions.map((session) => (
            <Option key={session._id} value={session._id}>
              {session.sessionname}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Event"
        name="event"
        rules={[{ required: true, message: "Please select an event!" }]}
      >
        <Select placeholder="Select an event">
          {events.map((event) => (
            <Option key={event._id} value={event._id}>
              {event.eventname}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Machine"
        name="machine"
        rules={[{ required: true, message: "Please select a machine!" }]}
      >
        <Select placeholder="Select a machine">
          {machines.map((machine) => (
            <Option key={machine._id} value={machine._id}>
              {machine.machinename}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="User"
        name="user"
        rules={[{ required: true, message: "Please select a user!" }]}
      >
        <Select placeholder="Select a user">
          {users.map((user) => (
            <Option key={user._id} value={user._id}>
              {user.username}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item label="Overlay Image" name="overlayImage">
        <ImageUpload
          setImageUrl={(url) => setOverlayImages([...overlayImages, url])}
        />
        {overlayImages.length > 0 &&
          overlayImages.map((url, index) => (
            <img
              key={index}
              src={url}
              alt="Overlay"
              style={{ marginTop: 10, width: "100%" }}
            />
          ))}
      </Form.Item>

      <Form.Item label="GIF Overlay" name="gifOverlay">
        <ImageUpload
          setImageUrl={(url) => setGifOverlays([...gifOverlays, url])}
        />
        {gifOverlays.length > 0 &&
          gifOverlays.map((url, index) => (
            <img
              key={index}
              src={url}
              alt="GIF Overlay"
              style={{ marginTop: 10, width: "100%" }}
            />
          ))}
      </Form.Item>

      <Form.Item label="Green Screen Background" name="greenScreenBackground">
        <ImageUpload
          setImageUrl={(url) =>
            setGreenScreenBackgrounds([...greenScreenBackgrounds, url])
          }
        />
        {greenScreenBackgrounds.length > 0 &&
          greenScreenBackgrounds.map((url, index) => (
            <img
              key={index}
              src={url}
              alt="Green Screen Background"
              style={{ marginTop: 10, width: "100%" }}
            />
          ))}
      </Form.Item>
      {/* Include GameSetting Component */}
      <GameSetting
        game={game}
        winners={winners}
        onGameChange={setGame}
        onWinnersChange={setWinners}
      />

      {/* Include PrintSettings Component */}
      <PrintSettings printSettings={printSettings}onChange={handlePrintSettingsChange} />

      {/* Include ShareSetting Component */}
      <ShareSetting
        smsConfig={smsConfig}
        onSmsChange={handleSmsChange}
        facebookConfig={facebookConfig}
        onFacebookChange={handleFacebookChange}
      />

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          Save Configuration
        </Button>
      </Form.Item>
    </Form>
  );
};

export default AddConfiguration;
